import React, {
	useEffect,
	useState
} from "react";
import PropTypes from "prop-types";
import ReactDatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { useClickOutside } from "hooks/useClickOutside";
import {
	Button,
	TextField
} from "@shopify/polaris";
import { formatDate } from "utils/dates";

import calendarIMG from "img/icons/misc/calendar.svg";
import clearIMG from "img/icons/misc/close-circle.svg";

import "./index.scss";

const Datepicker = (props) => {
	const {t} = useTranslation();
	const {
		onChange = () => {
		},
		isLimited = true,
		label = t("datepicker.dateRange"),
		prefix = <img
				src={calendarIMG}
				alt='icon calendar'
				style={{marginTop: 5}}
		/>,
		start,
		end,
		value,
		allowRange = true,
		placeholder,
		datepickerProps,
		isClearable = false,
		clearOnlyWhenHasValue = false,
		customFormatDate = formatDate,
		alignment = "left",
		labelDisabled,
		maxDatePeriodYear = 1,
		minDateOverride,
		maxDateOverride,
		disabled = false,
		requiredIndicator = false,
		error,
	} = props;

	const [active, setActive] = useState(false);
	const [renderDate, setRenderDate] = useState("");
	const [transactionDatetime, setTransactionDatetime] = useState(null);
	const [startDate, setStartDate] = useState(
			value ? new Date(value) : (start ? new Date(start) : minDateOverride ? new Date(minDateOverride) : null),
	);
	const [endDate, setEndDate] = useState(end ? new Date(end) : null);

	const pickerRef = useClickOutside(() => {
		setActive(false);
	});

	useEffect(() => {
		if (value) {
			const parsedValue = new Date(value);
			if (!isNaN(parsedValue)) {
				setStartDate(parsedValue);
			}
		} else if (value === null || value === undefined) {
			// Reset startDate when value is explicitly null/undefined
			setStartDate(null);
		} else if (start) {
			const parsedStart = new Date(start);
			if (!isNaN(parsedStart)) {
				setStartDate(parsedStart);
			}
		}
	}, [value, start]);

	const minDate = minDateOverride
			? new Date(minDateOverride)
			: isLimited
					? new Date(new Date().setFullYear(new Date().getFullYear() - 5))
					: null;

	const maxDate = maxDateOverride
			? new Date(maxDateOverride)
			: isLimited
					? new Date(new Date().setFullYear(new Date().getFullYear() + maxDatePeriodYear))
					: null;


	useEffect(() => {
		if (allowRange) {
			if (start && end) {
				setRenderDate(`${customFormatDate(start)} - ${customFormatDate(end)}`);
			} else {
				setRenderDate("");
			}
		} else {
			if (startDate) {
				setRenderDate(customFormatDate(startDate));
			} else if (value) {
				setRenderDate(customFormatDate(value));
			} else if (minDateOverride) {
				setRenderDate(customFormatDate(new Date(minDateOverride)));
			} else {
				setRenderDate(""); // Don't set default date, keep empty
			}
		}
	}, [allowRange, start, end, value, startDate, customFormatDate, minDateOverride]);

	const handleChange = (dates) => {
		if (!dates) {
			return;
		}

		if (allowRange) {
			const [rangeStart, rangeEnd] = dates;
			setStartDate(rangeStart);
			setEndDate(rangeEnd);
			onChange(rangeStart, rangeEnd);

			if (rangeStart && rangeEnd) {
				setRenderDate(
						`${customFormatDate(rangeStart)} - ${customFormatDate(rangeEnd)}`,
				);
			}
		} else {
			setStartDate(dates);
			onChange(dates);

			if (dates) {
				setRenderDate(customFormatDate(dates));
			}
		}

		setActive(false);
	};

	useEffect(() => {
		if (transactionDatetime) {
			const parsedDate = new Date(transactionDatetime);
			if (!isNaN(parsedDate)) {
				setStartDate(parsedDate);
			}
		}
	}, [transactionDatetime]);

	const handleDateChange = (startVal) => {
		if (startVal) {
			setTransactionDatetime(startVal.toISOString());
		}
	};

	const onClear = () => {
		if (allowRange) {
			onChange(null, null);
		} else {
			onChange(null);
		}
		setRenderDate("");
		setStartDate(new Date());
		setEndDate(null);
	};

	const showClearControl =
		Boolean(isClearable) &&
		(!clearOnlyWhenHasValue || String(renderDate || "").trim() !== "");

	const handleToday = () => {
		const today = new Date();
		setStartDate(today);
		setEndDate(null);
		onChange(today);
		setRenderDate(customFormatDate(today));
		setActive(false);
	};

	const handleInputChange = (inputValue) => {
		setRenderDate(inputValue);
		const parsedDate = new Date(inputValue);
		if (!isNaN(parsedDate)) {
			setStartDate(parsedDate);
			onChange(parsedDate);
		}
	};

	return (
			<div
					className='Datepicker'
					ref={pickerRef}
			>
				<TextField
						type='text'
						label={labelDisabled ? "" : label}
						requiredIndicator={requiredIndicator}
						error={error}
						prefix={prefix}
						value={renderDate || ""}
						onFocus={() => !disabled && setActive(true)}
						placeholder={placeholder}
						onChange={handleInputChange}
						autoComplete='off'
						disabled={disabled}
						suffix={
								showClearControl && (
										<img
												src={clearIMG}
												onClick={onClear}
												alt='clear'
												style={{
													width: "18px",
													height: "18px",
													cursor: "pointer",
													marginTop: "2px",
												}}
										/>
								)
						}
				/>
				{active && !disabled && (
						<div
								className='Datepicker-Wrapper'
								style={{[alignment]: 0}}
						>
							<ReactDatePicker
									selected={startDate}
									onChange={handleChange}
									startDate={startDate}
									endDate={endDate}
									inline
									selectsRange={allowRange}
									showYearDropdown
									minDate={minDate}
									maxDate={maxDate}
									{...datepickerProps}
							/>
							<Button
									onClick={handleToday}
									variant='primary'
							>
								{t("Today")}
							</Button>
						</div>
				)}
			</div>
	);
};

Datepicker.propTypes = {
	onChange: PropTypes.func,
	label: PropTypes.string,
	prefix: PropTypes.node,
	start: PropTypes.string,
	end: PropTypes.string,
	value: PropTypes.string,
	allowRange: PropTypes.bool,
	placeholder: PropTypes.string,
	datepickerProps: PropTypes.object,
	isClearable: PropTypes.bool,
	clearOnlyWhenHasValue: PropTypes.bool,
	customFormatDate: PropTypes.func,
	alignment: PropTypes.oneOf(["right", "left"]),
	disabled: PropTypes.bool,
	requiredIndicator: PropTypes.bool,
	error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

export default Datepicker;
